import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
} from 'shared/config/model/urls';

import React, {useEffect, useMemo} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {userReportAPI} from 'features/userReports/api/userReportAPI';
import {fetchMe, userSelector} from 'entities/user';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import NameDescriptionFields from 'shared/ui/Form/NameDescriptionFields/NameDescriptionFields';
import {
    MonitorSelectField,
    MONITORS_MAX,
    validateMonitorsSelection,
    buildMonitorLabels,
} from 'entities/monitor';
import HideBannerCheckbox from 'shared/ui/HideBannerCheckbox/HideBannerCheckbox';
import {NotifyChannels} from 'entities/notification';
import {UserReportPeriod, IUserReportMonitorRef} from 'features/userReports/model/UserReport/IUserReport';
import CreateUserReportType from 'features/userReports/model/UserReport/CreateUserReportType';
import {idsToIri, iriToId} from 'shared/api/iriHelper';
import {TypedDispatch} from 'shared/model/types';

import PeriodSelector from '../PeriodSelector/PeriodSelector';

import styles from './UserReportForm.module.scss';

const orderedMonitorUuids = (monitors: IUserReportMonitorRef[]): string[] =>
    [...monitors]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((m) => m.monitor_id)
        .filter(Boolean);

interface IUserReportFormValues {
    name: string;
    description: string;
    period: UserReportPeriod;
    hideBanner: boolean;
    notifyByEmail: boolean;
    telegramChats: string[];
    maxChats: string[];
    additionalEmails: string[];
    monitors: string[];
}

interface IUserReportFormProps {
    uuid?: string;
    onSuccess: () => void;
    onClose: () => void;
}

const UserReportForm: React.FC<IUserReportFormProps> = ({uuid, onSuccess, onClose}) => {
    const isNew = !uuid;
    const dispatch = useDispatch<TypedDispatch>();
    const user = useSelector(userSelector);

    const {data: fullUserReport} = userReportAPI.useGetUserReportQuery(uuid as string, {skip: isNew});

    const [addUserReport, {isLoading: isAdding}] = userReportAPI.useAddUserReportMutation();
    const [updateUserReport, {isLoading: isUpdating}] = userReportAPI.useUpdateUserReportMutation();

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: {errors, isValid},
    } = useForm<IUserReportFormValues>({
        mode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            period: 'M',
            hideBanner: false,
            notifyByEmail: false,
            telegramChats: [],
            maxChats: [],
            additionalEmails: [],
            monitors: [],
        },
    });

    register('monitors', {validate: validateMonitorsSelection});

    useEffect(() => {
        if (fullUserReport) {
            reset({
                name: fullUserReport.name,
                description: fullUserReport.description ?? '',
                period: fullUserReport.period,
                hideBanner: fullUserReport.hide_banner,
                notifyByEmail: fullUserReport.notify_by_email,
                telegramChats: (fullUserReport.telegram_chats ?? []).map((iri) => iriToId(iri) ?? iri),
                maxChats: (fullUserReport.max_chats ?? []).map((iri) => iriToId(iri) ?? iri),
                additionalEmails: (fullUserReport.additional_emails ?? []).map((iri) => iriToId(iri) ?? iri),
                monitors: orderedMonitorUuids(fullUserReport.monitors ?? []),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullUserReport?.uuid]);

    const monitorLabels = useMemo(
        () => buildMonitorLabels(fullUserReport?.monitors ?? []),
        [fullUserReport?.uuid], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const period = watch('period');
    const selectedMonitors = watch('monitors');

    const handleMonitorsChange = (next: string[]) => {
        setValue('monitors', next, {shouldValidate: true, shouldDirty: true});
    };

    const handlePeriodChange = (next: UserReportPeriod) => {
        setValue('period', next, {shouldValidate: true, shouldDirty: true});
    };

    const onSubmit: SubmitHandler<IUserReportFormValues> = async(values) => {
        const payload: CreateUserReportType = {
            name: values.name.trim(),
            description: values.description.trim(),
            period: values.period,
            hide_banner: values.hideBanner,
            is_active: isNew ? true : (fullUserReport?.is_active ?? true),
            notify_by_email: values.notifyByEmail,
            telegram_chats: idsToIri(values.telegramChats, TELEGRAM_CHATS_RESOURCE_NAME),
            max_chats: idsToIri(values.maxChats, MAX_CHATS_RESOURCE_NAME),
            additional_emails: idsToIri(values.additionalEmails, ADDITIONAL_EMAILS_RESOURCE_NAME),
            monitors: idsToIri(values.monitors, 'monitors'),
        };

        try {
            if (isNew) {
                await addUserReport(payload).unwrap();
                toast.info(t`Отчёт создан`);
                dispatch(fetchMe());
            } else {
                await updateUserReport({uuid: uuid as string, ...payload}).unwrap();
                toast.info(t`Изменения сохранены`);
            }
            onSuccess();
        } catch {
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper className={styles.section}>
                <NameDescriptionFields
                    control={control}
                    register={register}
                    nameError={errors?.name?.message}
                    descriptionError={errors?.description?.message}
                    namePlaceholder={t`Введите название отчёта`}
                    descriptionPlaceholder={t`Введите описание отчёта`}
                    descriptionRows={4}
                />

                <HideBannerCheckbox
                    register={register}
                    name="hideBanner"
                    enabled={!!user?.tariff.report_hide_banner_enabled}
                />
            </ModalBodyWrapper>

            <MonitorSelectField
                value={selectedMonitors}
                onChange={handleMonitorsChange}
                initialLabels={monitorLabels}
                error={errors?.monitors?.message}
                maxCount={MONITORS_MAX}
            />

            <ModalBodyWrapper className={styles.section}>
                <span className={styles.sectionTitle}>{t`Периодичность отправки`}</span>
                <PeriodSelector value={period} onChange={handlePeriodChange}/>
            </ModalBodyWrapper>

            <ModalBodyWrapper className={styles.section}>
                <span className={styles.sectionTitle}>{t`Каналы для получения`}</span>
                <NotifyChannels
                    control={control}
                    register={register}
                    emailFieldName="notifyByEmail"
                    additionalEmailsFieldName="additionalEmails"
                    telegramFieldName="telegramChats"
                    maxFieldName="maxChats"
                />
            </ModalBodyWrapper>

            <ModalBlockButton
                primaryButtonText={isNew ? t`Создать` : t`Сохранить`}
                primaryButtonType="submit"
                primaryButtonLoading={isAdding || isUpdating}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default UserReportForm;
